import { destroyStores, refreshBearcod, refreshStocks, useLegalEntities } from '@gozoki/api'
import {
  ACTIVE_ANALYTICS_STORE_STATUSES,
  type StoreStatus,
  type StoreTagDTO,
  type StoreType,
} from '@gozoki/api-types'
import { includesLowerCase } from '@gozoki/tools'
import { Menu, MenuItem, Paper, TextField, Typography } from '@mui/material'
import type { GridColDef, GridInitialState } from '@mui/x-data-grid'
import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { RotateCw } from 'lucide-react'
import { useMemo, useState } from 'react'
import { api } from '~/lib/tuyau'
import { AuthorizedButtonLink, withRoutePermissions } from '~/utils/rbac'
import { formatDateOnly } from '~/utils/types/formatDates'
import { storeStatusLabel, storeTypeLabel } from '~/utils/types/toFrench'
import Page from '../../../components/Page'
import StoreCSVExportButton from '../../../components/buttons/StoreCSVExportButton'
import {
  AuthorizedButton,
  AuthorizedIconButton,
} from '../../../components/communication/AuthorizedButton'
import { StoreTagChips } from '../../../components/routes/stores/StoreTagChips'
import { STORE_JOB_RUN, STORES_PAGE_READ } from '../../../components/routes/stores/tabs'
import SupplyReportButton from '../../../components/buttons/SupplyReportButton'
import SimpleDataGrid from '../../../components/containers/SimpleDataGrid'
import PlusIcon from '../../../components/icons/PlusIcon'
import { fonts } from '../../../utils/theme/fonts.theme'

export const Route = createFileRoute('/_authenticated/stores/')({
  staticData: {
    permission: STORES_PAGE_READ,
  },
  component: StoresPage,
})
withRoutePermissions(Route)

const initialFilterState: GridInitialState = {
  filter: {
    filterModel: {
      items: [
        {
          field: 'storeType',
          operator: 'equals',
          value: 'BOUTIQUE', // La valeur à filtrer
        },
      ],
    },
  },
}

const promoCampaignColumnDefs: GridColDef[] = [
  {
    field: 'label',
    flex: 1,
    headerName: 'Nom',
  },
  {
    field: 'address',
    flex: 1,
    headerName: 'Adresse',
    renderCell: (field) => field.value?.address ?? 'Non renseignée',
  },
  {
    field: 'id',
    flex: 1,
    headerName: 'ID',
  },
  {
    field: 'storeType',
    flex: 1,
    headerName: 'Type de boutique',
    renderCell: (field) => storeTypeLabel[field.value as StoreType],
    type: 'string',
    valueFormatter: (field) => storeTypeLabel[field.value as StoreType],
  },
  {
    field: 'status',
    flex: 1,
    headerName: 'Statut',
    renderCell: (field) => storeStatusLabel[field.value as StoreStatus],
    type: 'string',
    valueFormatter: (field) => storeStatusLabel[field.value as StoreStatus],
  },
  {
    field: 'tags',
    flex: 1,
    headerName: 'Tags',
    sortable: false,
    renderCell: (field) => (
      <StoreTagChips tags={field.value as StoreTagDTO[]} max={2} size="small" />
    ),
    valueFormatter: (field) =>
      ((field.value as StoreTagDTO[]) ?? []).map((tag) => tag.label).join(', '),
  },
  {
    field: 'legalEntity',
    flex: 1,
    headerName: 'Entreprise légale',
  },
  {
    field: 'openingDate',
    flex: 1,
    headerName: "Date d'ouverture",
    valueFormatter: (params) => formatDateOnly(params.value as string),
  },
]

function StoresPage() {
  const [storeSearch, setStoreSearch] = useState('')
  const navigate = useNavigate()

  const [storesSelection, setStoresSelection] = useState<number[]>([])
  const [refreshMenuAnchor, setRefreshMenuAnchor] = useState<null | HTMLElement>(null)

  const stores = useQuery(api.stores.index.queryOptions({}))

  const activeStores = useMemo(
    () =>
      stores.data
        ?.filter(
          (store) =>
            store.storeType === 'BOUTIQUE' &&
            (ACTIVE_ANALYTICS_STORE_STATUSES as readonly StoreStatus[]).includes(store.status),
        )
        .map((store) => store.id) ?? [],
    [stores.data],
  )

  const storesToExport = useMemo(() => {
    if (storesSelection.length > 0) {
      return storesSelection
    }
    return activeStores
  }, [storesSelection, activeStores])

  const { data: legalEntities } = useLegalEntities()

  // ********** QUERY TO DATAGRID TRANSFORMATION *********** //

  const filteredStoreRows =
    stores.data?.filter((store) => includesLowerCase(store.label, storeSearch)) ?? []

  const storeRows = filteredStoreRows.map((store) => ({
    ...store,
    legalEntity:
      legalEntities?.find((legalEntity) => legalEntity.id === store.legalEntityId)?.name ?? 'N/A',
  }))

  // *********************** HANDLERS  ********************* //

  const deleteStoresMut = useMutation({
    mutationFn: destroyStores,
  })

  const refreshStockMut = useMutation({
    mutationFn: refreshStocks,
  })
  const refreshBearcodMut = useMutation({
    mutationFn: refreshBearcod,
  })

  const navigateAndUpdateCampaign = async () => {
    navigate({ search: { storeId: storesSelection[0] }, to: '/stores/new' })
  }

  const navigateStoreDetails = (id: number) =>
    navigate({ params: { storeId: id }, to: '/stores/$storeId' })

  const storesDisabled = storesSelection.length === 0 || deleteStoresMut.isPending

  return (
    <Page sx={fonts.roboto}>
      <div style={{ display: 'flex' }}>
        <Typography style={fonts.pageTitle} flexGrow={1}>
          Boutiques
        </Typography>
        <AuthorizedButtonLink variant="contained" sx={{ marginRight: '16px' }} to="/stores/new">
          <PlusIcon />
          Ajouter une boutique
        </AuthorizedButtonLink>
        {/* Resynchronisations manuelles (CheckPoint, Bearcod) déclenchées depuis la liste. */}
        <AuthorizedIconButton
          permission={STORE_JOB_RUN}
          onClick={(e) => setRefreshMenuAnchor(e.currentTarget)}
          sx={{
            '&.Mui-disabled': { backgroundColor: 'action.disabledBackground' },
            '&:hover': { backgroundColor: 'primary.dark' },
            backgroundColor: 'primary.main',
            borderRadius: '50%',
            color: 'white',
            height: 40,
            width: 40,
          }}
        >
          <RotateCw />
        </AuthorizedIconButton>
        <Menu
          anchorEl={refreshMenuAnchor}
          open={Boolean(refreshMenuAnchor)}
          onClose={() => setRefreshMenuAnchor(null)}
        >
          <MenuItem
            onClick={() => {
              refreshStockMut.mutate()
              setRefreshMenuAnchor(null)
            }}
            disabled={refreshStockMut.isPending}
          >
            Rafraîchir les stocks CheckPoint
          </MenuItem>
          <MenuItem
            onClick={() => {
              refreshBearcodMut.mutate()
              setRefreshMenuAnchor(null)
            }}
            disabled={refreshBearcodMut.isPending}
          >
            Rafraîchir les données Bearcod
          </MenuItem>
        </Menu>
      </div>
      <Paper
        sx={{
          marginTop: '16px',
        }}
      >
        <div style={{ alignItems: 'center', display: 'flex', padding: '16px' }}>
          <TextField
            variant="outlined"
            label="Recherche"
            value={storeSearch}
            onChange={(e) => setStoreSearch(e.target.value)}
          />
          <div style={{ flexGrow: 1 }} />

          <AuthorizedButton
            permission={['edit', 'stores.store-list.store']}
            variant="contained"
            sx={{ height: '36px', marginRight: '16px' }}
            disabled={storesDisabled}
            onClick={navigateAndUpdateCampaign}
          >
            Modifier
          </AuthorizedButton>
          <StoreCSVExportButton
            selectedStores={storesToExport}
            sx={{ height: '36px', marginRight: '16px' }}
          />
          <SupplyReportButton />
        </div>
        <SimpleDataGrid
          columns={promoCampaignColumnDefs}
          rows={storeRows}
          loading={stores.isLoading}
          onRowSelectionModelChange={(newSelection) => setStoresSelection(newSelection as number[])}
          onRowClick={(row) => navigateStoreDetails(row.row.id)}
          initialState={initialFilterState}
        />
      </Paper>
    </Page>
  )
}
